import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Upload, FileText, Sparkles, Loader2, X, Wand2, Info,
} from "lucide-react";
import { toast } from "sonner";

const MAX_BYTES = 50 * 1024 * 1024;
const COUNT_OPTIONS = [3, 5, 7, 8, 10, 12, 15, 20];

function fmtSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Upload ONE chapter PDF; AI reads the whole thing and auto-splits it into
 * N progressive lessons. Once the upload finishes the heavy AI work is
 * handed off to `AiJobsContext` and tracked in the floating tray — so the
 * dialog can close immediately and the admin can queue another chapter
 * without waiting.
 */
export default function AiSplitPdfDialog({
    open,
    onOpenChange,
    chapterId,
    chapterTitle,
    onDone,
}) {
    const { trackJob } = useAiJobs();
    const [file, setFile] = useState(null);
    const [numLessons, setNumLessons] = useState(5);
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setNumLessons(5);
        setSubmitting(false);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleSelect = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!(f.name || "").toLowerCase().endsWith(".pdf")) {
            toast.error("Please choose a .pdf file.");
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error("PDF too large (max 50 MB). Compress it at ilovepdf.com first.");
            return;
        }
        setFile(f);
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.error("Pick a PDF first.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("num_lessons", String(numLessons));
            // Upload + extraction is bounded server-side, so a 60s axios
            // timeout is plenty for the POST. The long AI work runs as a
            // background job tracked by AiJobsContext.
            const { data: kick } = await api.post(
                `/chapters/${chapterId}/lessons/ai-split-pdf`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    timeout: 60 * 1000,
                },
            );
            const jobId = kick?.job_id;
            if (!jobId) {
                toast.error("Could not start AI split. Try again.");
                setSubmitting(false);
                return;
            }

            // Hand off to the global tray and close the dialog so the user
            // can queue another chapter immediately.
            trackJob({
                kind: "split",
                jobId,
                chapterId,
                chapterTitle,
                numLessons: kick.num_lessons || numLessons,
                totalBatches: kick.total_batches || 1,
                onDone,
            });
            const batches = kick.total_batches || 1;
            toast.success(
                batches > 1
                    ? `Queued "${chapterTitle}" — ${batches} batches of up to ${kick.batch_size || 5} lessons. Track progress in the bottom-right tray.`
                    : `Queued "${chapterTitle}". Track progress in the bottom-right tray.`,
            );
            reset();
            onOpenChange(false);
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "AI splitter failed.";
            toast.error(typeof msg === "string" ? msg : "AI splitter failed.");
            setSubmitting(false);
        }
    };

    const handleClose = (next) => {
        if (submitting) return; // don't allow close mid-upload
        onOpenChange(next);
        if (!next) reset();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl" data-testid="ai-split-pdf-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <Wand2 className="h-5 w-5 text-primary" />
                        AI: Split chapter PDF into lessons
                    </DialogTitle>
                    <DialogDescription>
                        Upload <strong>one PDF</strong> that contains your full chapter
                        content. The AI reads the document and breaks it into the number
                        of progressive lessons you choose — titles, body copy, and Key
                        Takeaways included. Lessons are saved as drafts in{" "}
                        <em>{chapterTitle}</em> for you to review before publishing.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* File picker */}
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-2">
                            Chapter PDF
                        </Label>
                        {!file ? (
                            <label
                                htmlFor="ai-split-pdf-file"
                                className="flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-border rounded-sm cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                                data-testid="ai-split-dropzone"
                            >
                                <Upload className="h-7 w-7 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                    Click to choose a PDF (max 50 MB)
                                </span>
                                <span className="text-xs text-muted-foreground">
                                    Digital text-based PDFs work best
                                </span>
                            </label>
                        ) : (
                            <div className="flex items-center gap-3 p-3 border border-border rounded-sm bg-card">
                                <FileText className="h-5 w-5 text-primary shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">
                                        {file.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {fmtSize(file.size)}
                                    </div>
                                </div>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => {
                                        setFile(null);
                                        if (inputRef.current) inputRef.current.value = "";
                                    }}
                                    disabled={submitting}
                                    aria-label="Remove file"
                                    data-testid="ai-split-remove-file"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                        <Input
                            ref={inputRef}
                            id="ai-split-pdf-file"
                            type="file"
                            accept="application/pdf"
                            onChange={handleSelect}
                            className="hidden"
                            data-testid="ai-split-file-input"
                        />
                    </div>

                    {/* Lesson count */}
                    <div>
                        <Label
                            htmlFor="ai-split-num-lessons"
                            className="text-xs uppercase tracking-wider text-muted-foreground font-semibold block mb-2"
                        >
                            Number of lessons to create
                        </Label>
                        <Select
                            value={String(numLessons)}
                            onValueChange={(v) => setNumLessons(Number(v))}
                            disabled={submitting}
                        >
                            <SelectTrigger
                                id="ai-split-num-lessons"
                                data-testid="ai-split-num-lessons"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {COUNT_OPTIONS.map((n) => (
                                    <SelectItem key={n} value={String(n)}>
                                        {n} lessons
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground mt-1.5">
                            The AI splits your PDF into progressive lessons covering the
                            whole chapter in order. Larger counts are processed in
                            background batches of 5.
                        </p>
                    </div>

                    {/* Speed expectation banner */}
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 rounded-sm">
                        <Sparkles className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                        <div className="text-[11px] text-amber-900 dark:text-amber-200 leading-relaxed">
                            5 lessons take ~45 seconds. 10+ lessons run in back-to-back
                            batches of 5 (about <strong>~1 minute per batch</strong>).
                            Each batch&apos;s lessons appear in the chapter as soon as it
                            finishes. New lessons save as <strong>unpublished drafts</strong>{" "}
                            for you to review.
                        </div>
                    </div>

                    {/* Handoff banner — sets expectation that the dialog will close */}
                    <div className="flex items-start gap-2 p-3 bg-primary/5 border border-primary/30 rounded-sm">
                        <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div className="text-[11px] text-foreground/80 leading-relaxed">
                            After you click <strong>Start</strong>, this dialog closes and
                            progress moves to the floating tray in the bottom-right
                            corner. You can immediately open another chapter and queue it
                            for AI splitting — multiple jobs run side-by-side.
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={() => handleClose(false)}
                        disabled={submitting}
                        data-testid="ai-split-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!file || submitting}
                        className="ai-shimmer gap-1.5"
                        data-testid="ai-split-submit"
                    >
                        {submitting ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Wand2 className="h-4 w-4 mr-1" />
                        )}
                        {submitting
                            ? "Uploading PDF…"
                            : `Start — split into ${numLessons} lessons`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
